import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n';
import './PWAInstallBanner.css';

const DISMISS_KEY = 'dancemance.pwa.install.dismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** True when running in iOS Safari (not already installed as PWA). */
function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  const isStandalone = (window.navigator as any).standalone === true;
  // Also allow iPadOS 13+ (desktop UA but with maxTouchPoints > 1)
  const isPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return (isIos || isPadOs) && !isStandalone;
}

type Platform = 'ios' | 'android' | null;

export function PWAInstallBanner() {
  const [platform, setPlatform] = useState<Platform>(null);
  const [visible, setVisible] = useState(false);
  const deferredPrompt = useRef<any>(null);

  useEffect(() => {
    if (isDismissed()) return;

    // Already installed as standalone — don't show
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if ((window.navigator as any).standalone === true) return;

    if (isIosSafari()) {
      setPlatform('ios');
      // Delay a few seconds so it doesn't pop immediately on load
      const t = setTimeout(() => setVisible(true), 3500);
      return () => clearTimeout(t);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setPlatform('android');
      const t = setTimeout(() => setVisible(true), 3500);
      return () => clearTimeout(t);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    setDismissed();
    setVisible(false);
  }

  async function install() {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
      if (outcome === 'accepted') {
        dismiss();
        return;
      }
    }
    dismiss();
  }

  if (!visible || !platform) return null;

  return (
    <div className={`pwa-banner pwa-banner--${platform}`} role="complementary" aria-label={t('pwa.install.title')}>
      <div className="pwa-banner__icon" aria-hidden="true">
        <img src="/apple-touch-icon.png" alt="" width={44} height={44} />
      </div>
      <div className="pwa-banner__body">
        <p className="pwa-banner__title">{t('pwa.install.title')}</p>
        <p className="pwa-banner__msg">
          {platform === 'ios' ? (
            <>
              {t('pwa.install.ios')}
              {/* Share icon inline reference */}
              <span className="pwa-banner__share-hint" aria-hidden="true"> ⬆</span>
            </>
          ) : (
            t('pwa.install.android')
          )}
        </p>
      </div>
      <div className="pwa-banner__actions">
        {platform === 'android' && (
          <button
            id="pwa-install-btn"
            type="button"
            className="pwa-banner__install-btn"
            onClick={install}
          >
            {t('pwa.install.cta')}
          </button>
        )}
        <button
          id="pwa-dismiss-btn"
          type="button"
          className="pwa-banner__close"
          aria-label={t('pwa.install.dismiss')}
          onClick={dismiss}
        >
          ×
        </button>
      </div>
    </div>
  );
}
